import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Search, Plus, AlertCircle, CheckCircle, Clock, RotateCcw, TrendingUp, Users, Store, Truck, MessageSquare, Zap, AlertTriangle, Trash2 } from 'lucide-react';
import DateFilter from '@/components/DateFilter';

export default function AdminTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [activeView, setActiveView] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [stats, setStats] = useState(null);
  const [dateFilter, setDateFilter] = useState(null);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  
  // Bulk selection state
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkAssignment, setBulkAssignment] = useState('');
  const [bulkPriority, setBulkPriority] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Create ticket form state
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    department: 'general',
    priority: 'medium',
    user_id: null,
    is_internal: false,
    created_by_admin: true
  });
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      
      if (departmentFilter !== 'all') {
        params.department = departmentFilter;
      }
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      if (priorityFilter) {
        params.priority = priorityFilter;
      }
      
      if (activeView === 'sla-breached') {
        params.sla_breached = true;
      }
      
      if (dateFilter) {
        if (typeof dateFilter === 'object' && dateFilter.type === 'custom') {
          params.date_from = dateFilter.from;
          params.date_to = dateFilter.to;
        } else {
          params.date_filter = dateFilter;
        }
      }
      
      const res = await api.get('/admin/tickets', { params });
      setTickets(res.data.tickets || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [page, departmentFilter, statusFilter, priorityFilter, activeView, dateFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/tickets/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load ticket stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Fetch users for dropdown
  const fetchUsers = useCallback(async (search = '') => {
    setLoadingUsers(true);
    try {
      const params = { page: 1, limit: 50, role: 'end_user' };
      if (search) params.search = search;
      const res = await api.get('/admin/users', { params });
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (showCreateTicket) {
      fetchUsers();
    }
  }, [showCreateTicket, fetchUsers]);

  // Keyboard shortcut for Ctrl+A (Select All)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+A or Cmd+A to select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        if (tickets.length > 0) {
          if (selectedTickets.length === tickets.length) {
            setSelectedTickets([]);
            toast.info('All tickets deselected');
          } else {
            setSelectedTickets(tickets.map(t => t.id));
            toast.info(`${tickets.length} tickets selected`);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tickets, selectedTickets]);

  const handleCreateTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const ticketData = {
        subject: newTicket.title.trim(),  // Backend expects 'subject'
        description: newTicket.description.trim(),
        category: newTicket.department,  // Backend expects 'category'
        priority: newTicket.priority,
        is_internal: newTicket.is_internal,
        created_by_admin: true,
        created_by_name: 'Admin User',
        created_by_email: 'admin@r2yn.com'
      };
      
      // Only add user_id if it's set
      if (newTicket.user_id) {
        ticketData.user_id = newTicket.user_id;
      }
      
      console.log('Creating ticket:', ticketData);
      const response = await api.post('/admin/tickets', ticketData);
      console.log('Ticket created:', response.data);
      
      if (newTicket.is_internal) {
        toast.success('Internal ticket created successfully!');
      } else {
        toast.success('Ticket created! AI is analyzing the request...');
      }
      
      setShowCreateTicket(false);
      setNewTicket({ 
        title: '', 
        description: '', 
        department: 'general', 
        priority: 'medium',
        user_id: null,
        is_internal: false,
        created_by_admin: true
      });
      setUserSearch('');
      
      // Refresh data with slight delay to allow AI processing
      setTimeout(async () => {
        await fetchTickets();
        await fetchStats();
      }, 1500);
    } catch (err) {
      console.error('Ticket creation error:', err);
      console.error('Error response:', err.response?.data);
      
      // Better error display
      const errorMsg = err.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        const fieldErrors = errorMsg.map(e => `${e.loc?.join('.')||'field'}: ${e.msg}`).join(', ');
        toast.error(`Validation error: ${fieldErrors}`);
      } else {
        toast.error(errorMsg || err.response?.data?.message || 'Failed to create ticket');
      }
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(tickets.map(t => t.id));
    }
  };

  const toggleSelectTicket = (ticketId) => {
    if (selectedTickets.includes(ticketId)) {
      setSelectedTickets(selectedTickets.filter(id => id !== ticketId));
    } else {
      setSelectedTickets([...selectedTickets, ticketId]);
    }
  };

  const handleBulkAction = () => {
    if (!bulkAction) {
      toast.error('Please select an action');
      return;
    }
    if (selectedTickets.length === 0) {
      toast.error('Please select at least one ticket');
      return;
    }

    // Set up confirmation
    setConfirmAction({
      type: bulkAction,
      count: selectedTickets.length,
      onConfirm: executeBulkAction
    });
    setShowConfirmDialog(true);
  };

  const executeBulkAction = async () => {
    try {
      if (bulkAction === 'delete') {
        // Delete tickets
        await Promise.all(selectedTickets.map(id => 
          api.delete(`/admin/tickets/${id}`)
        ));
        toast.success(`${selectedTickets.length} ticket(s) deleted successfully`);
      } else if (bulkAction === 'change_status') {
        if (!bulkStatus) {
          toast.error('Please select a status');
          return;
        }
        // Update status for all selected tickets
        await Promise.all(selectedTickets.map(id =>
          api.put(`/admin/tickets/${id}`, { status: bulkStatus })
        ));
        toast.success(`${selectedTickets.length} ticket(s) status updated to ${bulkStatus}`);
      } else if (bulkAction === 'change_assignment') {
        if (!bulkAssignment) {
          toast.error('Please select an assignment');
          return;
        }
        // Update assignment for all selected tickets
        await Promise.all(selectedTickets.map(id =>
          api.put(`/admin/tickets/${id}`, { assigned_to: bulkAssignment })
        ));
        toast.success(`${selectedTickets.length} ticket(s) assigned to ${bulkAssignment}`);
      } else if (bulkAction === 'change_priority') {
        if (!bulkPriority) {
          toast.error('Please select a priority');
          return;
        }
        // Update priority for all selected tickets
        await Promise.all(selectedTickets.map(id =>
          api.put(`/admin/tickets/${id}`, { priority: bulkPriority })
        ));
        toast.success(`${selectedTickets.length} ticket(s) priority updated to ${bulkPriority}`);
      } else if (bulkAction === 'export') {
        // Export selected tickets to CSV
        exportTicketsToCSV(selectedTickets);
        toast.success(`${selectedTickets.length} ticket(s) exported successfully`);
        setSelectedTickets([]);
        setBulkAction('');
        setShowConfirmDialog(false);
        return; // Don't refresh for export
      }

      // Clear selections and refresh
      setSelectedTickets([]);
      setShowBulkActions(false);
      setBulkAction('');
      setBulkStatus('');
      setBulkAssignment('');
      setBulkPriority('');
      setShowConfirmDialog(false);
      await fetchTickets();
      await fetchStats();
    } catch (err) {
      toast.error('Bulk action failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const exportTicketsToCSV = (ticketIds) => {
    const selectedData = tickets.filter(t => ticketIds.includes(t.id));
    
    // Create CSV content
    const headers = ['Ticket Number', 'Title', 'Department', 'Priority', 'Status', 'Assigned To', 'Created By', 'Created At'];
    const rows = selectedData.map(t => [
      t.ticket_number,
      `"${t.title.replace(/"/g, '""')}"`, // Escape quotes
      t.department,
      t.priority,
      t.status,
      t.assigned_to || 'Unassigned',
      t.created_by_name,
      new Date(t.created_at).toLocaleString()
    ]);
    
    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `r2yn-tickets-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-rose-50 text-rose-800 border-rose-200',
      high: 'bg-orange-50 text-orange-800 border-orange-200',
      medium: 'bg-blue-50 text-blue-800 border-blue-200',
      low: 'bg-slate-50 text-slate-800 border-slate-200'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      open: { classes: 'bg-blue-50 border-blue-200 text-blue-800', label: 'Open' },
      in_progress: { classes: 'bg-amber-50 border-amber-200 text-amber-800', label: 'In Progress' },
      pending: { classes: 'bg-purple-50 border-purple-200 text-purple-800', label: 'Pending' },
      resolved: { classes: 'bg-emerald-50 border-emerald-200 text-emerald-800', label: 'Resolved' },
      closed: { classes: 'bg-slate-50 border-slate-200 text-slate-800', label: 'Closed' }
    };
    const config = statusMap[status] || statusMap.open;
    return <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-semibold ${config.classes}`}>{config.label}</span>;
  };

  const isSLABreached = (ticket) => {
    if (ticket.status === 'resolved' || ticket.status === 'closed') return false;
    const now = new Date();
    const responseDue = new Date(ticket.sla_response_due);
    const resolutionDue = new Date(ticket.sla_resolution_due);
    return (!ticket.first_response_at && responseDue < now) || (!ticket.resolved_at && resolutionDue < now);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-font="display">Ticket Management</h1>
          <p className="text-muted-foreground">HubSpot-style enterprise support system with AI auto-resolution</p>
        </div>
        <Button onClick={() => setShowCreateTicket(true)} data-testid="create-ticket-button">
          <Plus className="h-4 w-4 mr-2" />
          Create Ticket
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <>
          <div className="grid gap-4 md:grid-cols-6">
            <Card className="hover:shadow-md transition-shadow cursor-pointer stats-card" onClick={() => { setActiveView('all'); setStatusFilter(null); setDepartmentFilter('all'); setPage(1); }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_tickets || 0}</div>
                <p className="text-xs text-muted-foreground">All time</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow cursor-pointer stats-card" onClick={() => { setActiveView('all'); setStatusFilter('open'); setDepartmentFilter('all'); setPage(1); }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Open</CardTitle>
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.open_tickets || 0}</div>
                <p className="text-xs text-muted-foreground">Awaiting response</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow cursor-pointer stats-card" onClick={() => { setActiveView('all'); setStatusFilter('in_progress'); setDepartmentFilter('all'); setPage(1); }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">In Progress</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.in_progress || 0}</div>
                <p className="text-xs text-muted-foreground">Being worked on</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow cursor-pointer stats-card" onClick={() => { setActiveView('all'); setStatusFilter('resolved'); setDepartmentFilter('all'); setPage(1); }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Resolved</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.resolved_tickets || 0}</div>
                <p className="text-xs text-muted-foreground">Successfully closed</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow cursor-pointer stats-card" onClick={() => { setActiveView('sla-breached'); setStatusFilter(null); setDepartmentFilter('all'); setPage(1); }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">SLA Breached</CardTitle>
                <AlertTriangle className="h-4 w-4 text-rose-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-rose-600">{stats.sla_breached || 0}</div>
                <p className="text-xs text-muted-foreground">Requires attention</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow stats-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">AI Resolved</CardTitle>
                <Zap className="h-4 w-4 text-amber-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">{stats.ai_resolved || 0}</div>
                <p className="text-xs text-muted-foreground">Auto-handled by AI</p>
              </CardContent>
            </Card>
          </div>

          {/* AI & Assignment Info Card */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="flex items-center gap-2 font-semibold text-blue-900 mb-2">
                    <Zap className="h-4 w-4" />
                    AI Auto-Resolution
                  </div>
                  <ul className="space-y-1 text-blue-800">
                    <li>• AI analyzes tickets instantly after creation</li>
                    <li>• Provides responses from knowledge base</li>
                    <li>• <strong>Auto-resolves if confidence ≥ 85%</strong></li>
                    <li>• Look for ⚡ icon in ticket list</li>
                  </ul>
                </div>
                <div>
                  <div className="flex items-center gap-2 font-semibold text-blue-900 mb-2">
                    <Users className="h-4 w-4" />
                    Auto-Assignment Rules
                  </div>
                  <ul className="space-y-1 text-blue-800">
                    <li>• <strong>Drivers</strong> → Driver Support Team</li>
                    <li>• <strong>Customers</strong> → Customer Success Team</li>
                    <li>• <strong>Vendors</strong> → Vendor Relations Team</li>
                    <li>• <strong>General/Tech</strong> → Technical Support Team</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Department Tabs */}
      <Tabs value={departmentFilter} onValueChange={(value) => { setDepartmentFilter(value); setPage(1); }} className="w-full">
        <TabsList className="grid w-full grid-cols-5 h-auto p-1 bg-muted">
          <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2">
            All Departments
          </TabsTrigger>
          <TabsTrigger value="drivers" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2">
            <Truck className="h-4 w-4 mr-2" />
            Drivers
          </TabsTrigger>
          <TabsTrigger value="customers" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2">
            <Users className="h-4 w-4 mr-2" />
            Customers
          </TabsTrigger>
          <TabsTrigger value="vendors" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2">
            <Store className="h-4 w-4 mr-2" />
            Vendors
          </TabsTrigger>
          <TabsTrigger value="general" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2">
            <MessageSquare className="h-4 w-4 mr-2" />
            General/Tech
          </TabsTrigger>
        </TabsList>

        <TabsContent value={departmentFilter} className="mt-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tickets by title, ID, or description..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-9"
                  />
                </div>
                <Select value={priorityFilter || 'all'} onValueChange={(v) => setPriorityFilter(v === 'all' ? null : v)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => { setSearch(''); setDateFilter(null); setStatusFilter(null); setPriorityFilter(null); setPage(1); toast.success('Filters reset'); }}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
                <DateFilter 
                  value={dateFilter}
                  onChange={(value) => { setDateFilter(value); setPage(1); }}
                  className="w-[200px]"
                />
              </div>

              {/* Bulk Actions Bar */}
              {selectedTickets.length > 0 && (
                <Card className="border-[hsl(351,96%,31%)] bg-[hsl(351,96%,31%,0.05)]">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="font-medium">{selectedTickets.length} ticket(s) selected</span>
                        <Select value={bulkAction} onValueChange={setBulkAction}>
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Select action" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="change_status">Change Status</SelectItem>
                            <SelectItem value="change_assignment">Change Assignment</SelectItem>
                            <SelectItem value="change_priority">Change Priority</SelectItem>
                            <SelectItem value="export">Export to CSV</SelectItem>
                            <SelectItem value="delete">Delete</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {bulkAction === 'change_status' && (
                          <Select value={bulkStatus} onValueChange={setBulkStatus}>
                            <SelectTrigger className="w-[180px]">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        
                        {bulkAction === 'change_assignment' && (
                          <Select value={bulkAssignment} onValueChange={setBulkAssignment}>
                            <SelectTrigger className="w-[220px]">
                              <SelectValue placeholder="Select team" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="customer-success-team">Customer Success Team</SelectItem>
                              <SelectItem value="driver-support-team">Driver Support Team</SelectItem>
                              <SelectItem value="vendor-relations-team">Vendor Relations Team</SelectItem>
                              <SelectItem value="technical-support-team">Technical Support Team</SelectItem>
                              <SelectItem value="billing-team">Billing Team</SelectItem>
                              <SelectItem value="escalation-team">Escalation Team</SelectItem>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        
                        {bulkAction === 'change_priority' && (
                          <Select value={bulkPriority} onValueChange={setBulkPriority}>
                            <SelectTrigger className="w-[180px]">
                              <SelectValue placeholder="Select priority" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="critical">Critical</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        
                        <Button onClick={handleBulkAction} disabled={!bulkAction}>
                          {bulkAction === 'export' ? 'Export' : 'Apply'}
                        </Button>
                        <Button variant="outline" onClick={() => setSelectedTickets([])}>
                          Clear Selection
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No tickets found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-sm font-medium text-muted-foreground">
                          <th className="pb-3 pl-4 w-12">
                            <Checkbox
                              checked={selectedTickets.length === tickets.length && tickets.length > 0}
                              onCheckedChange={toggleSelectAll}
                              data-testid="select-all-checkbox"
                            />
                          </th>
                          <th className="pb-3">#</th>
                          <th className="pb-3">Title</th>
                          <th className="pb-3">Department</th>
                          <th className="pb-3">Priority</th>
                          <th className="pb-3">Status</th>
                          <th className="pb-3">Assigned To</th>
                          <th className="pb-3">Created By</th>
                          <th className="pb-3">SLA</th>
                          <th className="pb-3">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.map((ticket) => (
                          <tr 
                            key={ticket.id}
                            className="border-b last:border-0 hover:bg-[hsl(351,96%,31%,0.02)] hover:border-l-2 hover:border-l-[hsl(351,96%,31%,0.3)] transition-all duration-200"
                            data-testid={`ticket-row-${ticket.id}`}
                          >
                            <td className="py-3 pl-4" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedTickets.includes(ticket.id)}
                                onCheckedChange={() => toggleSelectTicket(ticket.id)}
                                data-testid={`select-ticket-${ticket.id}`}
                              />
                            </td>
                            <td className="py-3 font-medium cursor-pointer" onClick={() => navigate(`/admin/tickets/${ticket.id}`)}>
                              <div className="flex items-center gap-2">
                                {ticket.ai_resolved && <Zap className="h-4 w-4 text-amber-600" title="AI Resolved" />}
                                #{ticket.ticket_number}
                              </div>
                            </td>
                            <td className="py-3 cursor-pointer" onClick={() => navigate(`/admin/tickets/${ticket.id}`)}>
                              <div className="font-medium">{ticket.title}</div>
                              <div className="text-xs text-muted-foreground line-clamp-1">{ticket.description}</div>
                            </td>
                            <td className="py-3 capitalize cursor-pointer" onClick={() => navigate(`/admin/tickets/${ticket.id}`)}>{ticket.department}</td>
                            <td className="py-3 cursor-pointer" onClick={() => navigate(`/admin/tickets/${ticket.id}`)}>
                              <Badge variant="outline" className={`${getPriorityColor(ticket.priority)} border text-xs`}>
                                {ticket.priority.toUpperCase()}
                              </Badge>
                            </td>
                            <td className="py-3 cursor-pointer" onClick={() => navigate(`/admin/tickets/${ticket.id}`)}>{getStatusBadge(ticket.status)}</td>
                            <td className="py-3 text-sm text-muted-foreground cursor-pointer" onClick={() => navigate(`/admin/tickets/${ticket.id}`)}>
                              {ticket.assigned_to || 'Unassigned'}
                            </td>
                            <td className="py-3 text-sm cursor-pointer" onClick={() => navigate(`/admin/tickets/${ticket.id}`)}>{ticket.created_by_name}</td>
                            <td className="py-3 cursor-pointer" onClick={() => navigate(`/admin/tickets/${ticket.id}`)}>
                              {isSLABreached(ticket) ? (
                                <Badge variant="outline" className="bg-rose-50 border-rose-200 text-rose-800 text-xs">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Breached
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  On Track
                                </span>
                              )}
                            </td>
                            <td className="py-3 text-sm text-muted-foreground cursor-pointer" onClick={() => navigate(`/admin/tickets/${ticket.id}`)}>
                              {new Date(ticket.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        Page {page} of {totalPages}
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                        >
                          Previous
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Internal Ticket Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md border">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-internal"
                  checked={newTicket.is_internal}
                  onChange={(e) => setNewTicket({ ...newTicket, is_internal: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="is-internal" className="cursor-pointer font-medium">
                  Internal Ticket (Team Use Only)
                </Label>
              </div>
              {newTicket.is_internal && (
                <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200">
                  No AI Response
                </Badge>
              )}
            </div>

            {/* User Selection (On Behalf Of) */}
            {!newTicket.is_internal && (
              <div className="space-y-2">
                <Label htmlFor="ticket-user">Create On Behalf Of (Optional)</Label>
                <Select 
                  value={newTicket.user_id || 'none'} 
                  onValueChange={(v) => setNewTicket({ ...newTicket, user_id: v === 'none' ? null : v })}
                >
                  <SelectTrigger id="ticket-user">
                    <SelectValue placeholder="Select a user..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No specific user --</SelectItem>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name || user.email} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Select a user if you're creating this ticket on their behalf
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="ticket-title">Title *</Label>
              <Input
                id="ticket-title"
                placeholder="Brief description of the issue..."
                value={newTicket.title}
                onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ticket-description">Description *</Label>
              <Textarea
                id="ticket-description"
                placeholder="Provide detailed information about the issue..."
                rows={5}
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ticket-department">Department</Label>
                <Select value={newTicket.department} onValueChange={(v) => setNewTicket({ ...newTicket, department: v })}>
                  <SelectTrigger id="ticket-department">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drivers">Drivers</SelectItem>
                    <SelectItem value="customers">Customers</SelectItem>
                    <SelectItem value="vendors">Vendors</SelectItem>
                    <SelectItem value="general">General/Tech</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ticket-priority">Priority</Label>
                <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}>
                  <SelectTrigger id="ticket-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!newTicket.is_internal && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                <div className="flex items-start gap-2">
                  <Zap className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>AI Assistant Enabled:</strong> Our AI will analyze this ticket and attempt to provide an instant solution. If confidence ≥ 85%, the ticket will be auto-resolved.
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateTicket(false);
              setNewTicket({ 
                title: '', 
                description: '', 
                department: 'general', 
                priority: 'medium',
                user_id: null,
                is_internal: false,
                created_by_admin: true
              });
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreateTicket}>
              Create Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog - HubSpot Style */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Action
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {confirmAction?.type === 'delete' ? (
              <div className="space-y-3">
                <p className="text-sm">
                  You are about to <span className="font-bold text-rose-600">permanently delete {confirmAction.count} ticket(s)</span>.
                </p>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. All ticket data, comments, and history will be permanently removed.
                </p>
              </div>
            ) : confirmAction?.type === 'change_status' ? (
              <div className="space-y-3">
                <p className="text-sm">
                  You are about to change the status of <span className="font-bold">{confirmAction.count} ticket(s)</span> to <span className="font-bold capitalize">{bulkStatus?.replace('_', ' ')}</span>.
                </p>
                <p className="text-sm text-muted-foreground">
                  This will update all selected tickets. Are you sure you want to continue?
                </p>
              </div>
            ) : confirmAction?.type === 'change_assignment' ? (
              <div className="space-y-3">
                <p className="text-sm">
                  You are about to assign <span className="font-bold">{confirmAction.count} ticket(s)</span> to <span className="font-bold">{bulkAssignment}</span>.
                </p>
                <p className="text-sm text-muted-foreground">
                  This will update the assignment for all selected tickets.
                </p>
              </div>
            ) : confirmAction?.type === 'change_priority' ? (
              <div className="space-y-3">
                <p className="text-sm">
                  You are about to change the priority of <span className="font-bold">{confirmAction.count} ticket(s)</span> to <span className="font-bold capitalize">{bulkPriority}</span>.
                </p>
                <p className="text-sm text-muted-foreground">
                  This will update the priority and recalculate SLA for all selected tickets.
                </p>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (confirmAction?.onConfirm) {
                  confirmAction.onConfirm();
                }
              }}
              className={confirmAction?.type === 'delete' ? 'bg-rose-600 hover:bg-rose-700' : ''}
            >
              {confirmAction?.type === 'delete' ? 'Delete Tickets' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
